import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart3,
  AlertCircle,
  Users,
  Clock,
  ChevronLeft,
  ChevronRight,
  Search,
  Download,
  RefreshCw,
  CheckCircle,
  XCircle,
  Loader2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Stat Card Component
const StatCard = ({ title, value, previousValue, icon: Icon, format = 'number' }) => {
  const formatValue = (val) => {
    if (format === 'duration') {
      return val >= 1000 ? `${(val / 1000).toFixed(1)}s` : `${val}ms`;
    }
    return val?.toLocaleString() || '0';
  };

  const getChange = () => {
    if (!previousValue || previousValue === 0) return null;
    const change = ((value - previousValue) / previousValue) * 100;
    return change;
  };

  const change = getChange();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-500 dark:text-gray-400">{title}</span>
        <Icon className="w-4 h-4 text-gray-400" />
      </div>
      <div className="flex items-end gap-2">
        <span className="text-2xl font-semibold text-gray-900 dark:text-white">
          {formatValue(value)}
        </span>
        {change !== null && (
          <span className={`text-sm flex items-center ${
            change > 0
              ? title === 'Errors' ? 'text-red-500' : 'text-green-500'
              : title === 'Errors' ? 'text-green-500' : 'text-red-500'
          }`}>
            {change > 0 ? '↑' : '↓'} {Math.abs(change).toFixed(0)}%
          </span>
        )}
      </div>
    </div>
  );
};

// Recent Errors Component
const RecentErrors = ({ errors, loading, onViewAll }) => {
  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-500" />
          <h3 className="font-medium text-gray-900 dark:text-white">Recent Errors</h3>
        </div>
        <button
          onClick={onViewAll}
          className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
        >
          View all →
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      ) : errors.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
          <p className="text-sm">No errors in this period</p>
        </div>
      ) : (
        <div className="space-y-3">
          {errors.map((error, idx) => (
            <div key={idx} className="border-l-2 border-red-400 pl-3 py-1">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500 dark:text-gray-400">{formatTime(error.created_at)}</span>
                <span className="text-gray-700 dark:text-gray-300">{error.user_email}</span>
                <span className={`px-1.5 py-0.5 rounded text-xs ${
                  error.mode === 'virgo'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                    : 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                }`}>
                  {error.mode}
                </span>
              </div>
              <p className="text-sm text-red-600 dark:text-red-400 truncate mt-1">
                {error.error_message || 'Unknown error'}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Audit Table Component
const AuditTable = ({ logs, loading, page, pages, total, onPageChange, filters, onFilterChange }) => {
  const [expandedRow, setExpandedRow] = useState(null);

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (ms) => {
    if (!ms) return '-';
    return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Filters */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search questions..."
              value={filters.q || ''}
              onChange={(e) => onFilterChange({ ...filters, q: e.target.value })}
              className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={filters.status || ''}
            onChange={(e) => onFilterChange({ ...filters, status: e.target.value })}
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            <option value="success">Success</option>
            <option value="error">Error</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            value={filters.mode || ''}
            onChange={(e) => onFilterChange({ ...filters, mode: e.target.value })}
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Modes</option>
            <option value="virgo">Virgo</option>
            <option value="taurus">Taurus</option>
          </select>
          <input
            type="text"
            placeholder="Filter by email..."
            value={filters.user_email || ''}
            onChange={(e) => onFilterChange({ ...filters, user_email: e.target.value })}
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Time</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">User</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Mode</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Source</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Duration</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" />
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  No audit logs found
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                  >
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {formatTime(log.created_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      {log.user_email || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        log.mode === 'virgo'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                          : 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                      }`}>
                        {log.mode}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      {log.source_name || '-'}
                    </td>
                    <td className="px-4 py-3">
                      {log.status === 'success' ? (
                        <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm">success</span>
                        </span>
                      ) : log.status === 'error' ? (
                        <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                          <XCircle className="w-4 h-4" />
                          <span className="text-sm">error</span>
                        </span>
                      ) : (
                        <span className="text-sm text-gray-500">{log.status}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      {formatDuration(log.details?.duration_ms)}
                    </td>
                    <td className="px-4 py-3">
                      {expandedRow === log.id ? (
                        <ChevronUp className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </td>
                  </tr>
                  {expandedRow === log.id && (
                    <tr className="bg-gray-50 dark:bg-gray-900">
                      <td colSpan={7} className="px-4 py-3">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Question:</span>
                            <p className="text-gray-900 dark:text-gray-100 mt-1">
                              {log.details?.question || '-'}
                            </p>
                          </div>
                          {log.details?.error_message && (
                            <div>
                              <span className="text-gray-500 dark:text-gray-400">Error:</span>
                              <p className="text-red-600 dark:text-red-400 mt-1">
                                {log.details.error_message}
                              </p>
                            </div>
                          )}
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">IP Address:</span>
                            <p className="text-gray-900 dark:text-gray-100 mt-1">
                              {log.details?.ip_address || '-'}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Session ID:</span>
                            <p className="text-gray-900 dark:text-gray-100 mt-1 font-mono text-xs">
                              {log.details?.session_id || '-'}
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <span className="text-sm text-gray-500 dark:text-gray-400">
          Showing {logs.length} of {total} entries
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Page {page} of {pages}
          </span>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pages}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Audit Dashboard Component
const AuditDashboard = ({ token, showAll = false }) => {
  const [period, setPeriod] = useState('7d');
  const [stats, setStats] = useState(null);
  const [recentErrors, setRecentErrors] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [errorsLoading, setErrorsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({});
  const filterTimeoutRef = React.useRef(null);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
    };
  }, []);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/audit/stats?period=${period}&all=${showAll}`, { headers });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch audit stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [period, token, showAll]);

  const fetchRecentErrors = useCallback(async () => {
    setErrorsLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/audit/recent-errors?limit=5&all=${showAll}`, { headers });
      setRecentErrors(response.data);
    } catch (error) {
      console.error('Failed to fetch recent errors:', error);
    } finally {
      setErrorsLoading(false);
    }
  }, [token, showAll]);

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams({ page, limit: 25, all: showAll });
      if (filters.status) params.append('status', filters.status);
      if (filters.mode) params.append('mode', filters.mode);
      if (filters.user_email) params.append('user_email', filters.user_email);
      if (filters.q) params.append('q', filters.q);

      const response = await axios.get(`${API}/audit/logs?${params}`, { headers });
      setLogs(response.data.data);
      setPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLogsLoading(false);
    }
  }, [page, filters, token, showAll]);

  // Initial load
  useEffect(() => {
    fetchStats();
    fetchRecentErrors();
  }, [fetchStats, fetchRecentErrors]);

  // Fetch logs when page or filters change
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Handle filter change (debounced via useEffect dependency)
  const handleFilterChange = (newFilters) => {
    if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
    filterTimeoutRef.current = setTimeout(() => {
      setFilters(newFilters);
      setPage(1); // Reset to first page on filter change
    }, 300);
  };

  const handleRefresh = () => {
    fetchStats();
    fetchRecentErrors();
    fetchLogs();
  };

  const handleViewAllErrors = () => {
    setFilters({ status: 'error' });
    setPage(1);
  };

  const handleExportCSV = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams({ page: 1, limit: 1000, all: showAll });
      if (filters.status) params.append('status', filters.status);
      if (filters.mode) params.append('mode', filters.mode);
      if (filters.user_email) params.append('user_email', filters.user_email);
      if (filters.q) params.append('q', filters.q);

      const response = await axios.get(`${API}/audit/logs?${params}`, { headers });
      const data = response.data.data;

      // Convert to CSV
      const csvRows = [
        ['Time', 'User', 'Mode', 'Source', 'Status', 'Duration (ms)', 'Question', 'Error'].join(','),
        ...data.map(log => [
          log.created_at,
          log.user_email || '',
          log.mode,
          log.source_name || '',
          log.status,
          log.details?.duration_ms || '',
          `"${(log.details?.question || '').replace(/"/g, '""')}"`,
          `"${(log.details?.error_message || '').replace(/"/g, '""')}"`
        ].join(','))
      ];

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export CSV:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-5 h-5 text-blue-500" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Audit Dashboard
            {showAll && <span className="ml-2 text-sm font-normal text-gray-500">(All Organizations)</span>}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          >
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
          <button
            onClick={handleRefresh}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard
          title="Total Queries"
          value={stats?.current?.total_queries || 0}
          previousValue={stats?.previous?.total_queries}
          icon={BarChart3}
        />
        <StatCard
          title="Errors"
          value={stats?.current?.errors || 0}
          previousValue={stats?.previous?.errors}
          icon={AlertCircle}
        />
        <StatCard
          title="Active Users"
          value={stats?.current?.active_users || 0}
          previousValue={stats?.previous?.active_users}
          icon={Users}
        />
        <StatCard
          title="Avg Duration"
          value={stats?.current?.avg_duration_ms || 0}
          previousValue={stats?.previous?.avg_duration_ms}
          icon={Clock}
          format="duration"
        />
      </div>

      {/* Recent Errors */}
      <RecentErrors
        errors={recentErrors}
        loading={errorsLoading}
        onViewAll={handleViewAllErrors}
      />

      {/* Activity Table */}
      <div>
        <h3 className="text-md font-medium text-gray-900 dark:text-white mb-3">All Activity</h3>
        <AuditTable
          logs={logs}
          loading={logsLoading}
          page={page}
          pages={pages}
          total={total}
          onPageChange={setPage}
          filters={filters}
          onFilterChange={handleFilterChange}
        />
      </div>
    </div>
  );
};

export default AuditDashboard;
